<script setup lang="ts">
  import { computed } from "vue";
  import { RotateCcw } from "lucide-vue-next";
  import { Avatar, AvatarFallback } from "@/components/ui/avatar";
  import { Badge } from "@/components/ui/badge";
  import { Button } from "@/components/ui/button";
  import { Spinner } from "@/components/ui/spinner";
  import { Tooltip } from "@/components/ui/tooltip";
  import { clockFrom, relativeTimeFrom } from "@utils/time";
  import type { Snapshot } from "@/types";

  const props = defineProps<{
    versions: Snapshot[];
    isLast: boolean;
    currentId: number;
    canRestore: boolean;
    restoreDisabledReason: string | null;
    restoringId: number | null;
  }>();

  const emit = defineEmits<{ restore: [snapshot: Snapshot] }>();

  const latest = computed(() => props.versions[0]);
  const dotClass = computed(
    () =>
      ({
        published: "bg-emerald-500",
        restored: "bg-sky-500",
        created: "bg-slate-400",
        saved: "bg-primary/60",
      })[latest.value.reason],
  );
  const reasonBadge = (snapshot: Snapshot) =>
    ({
      published: { label: "Published", variant: "success" },
      restored: { label: "Restored", variant: "info" },
      created: { label: "Created", variant: "neutral" },
      saved: null,
    })[snapshot.reason];
</script>

<template>
  <div
    class="relative pl-7"
    :class="isLast ? 'pb-1' : 'pb-6'">
    <span
      v-if="!isLast"
      class="absolute left-[7px] top-4 bottom-0 w-px bg-border" />
    <span
      class="absolute left-0 top-1.5 size-[15px] rounded-full border-[3px] border-background ring-1 ring-border"
      :class="dotClass" />

    <div class="flex items-center gap-2 text-sm">
      <Avatar class="size-6">
        <AvatarFallback class="text-3xs bg-secondary text-muted-foreground">{{ latest.user?.initials ?? "—" }}</AvatarFallback>
      </Avatar>
      <span class="font-medium truncate">{{ latest.user?.full_name ?? "System" }}</span>
      <Badge
        v-if="latest.origin === 'mcp'"
        variant="purple"
        class="text-3xs">
        Claude
      </Badge>
      <span
        class="ml-auto text-xs text-muted-foreground whitespace-nowrap"
        :title="new Date(latest.updated_at).toLocaleString()">
        {{ relativeTimeFrom(latest.updated_at) }}
      </span>
    </div>

    <ul class="mt-2 space-y-0.5">
      <li
        v-for="snapshot in versions"
        :key="snapshot.id"
        class="group flex items-center gap-2 rounded-md px-2 py-1.5 -mx-2 hover:bg-muted/60"
        :class="{ 'bg-primary/5': snapshot.id === currentId }">
        <span class="w-11 text-xs tabular-nums text-muted-foreground">{{ clockFrom(snapshot.updated_at) }}</span>
        <Badge
          v-if="reasonBadge(snapshot)"
          :variant="reasonBadge(snapshot)?.variant"
          class="text-3xs">
          {{ reasonBadge(snapshot)?.label }}
        </Badge>
        <span
          v-if="snapshot.id === currentId"
          class="text-2xs font-medium text-primary">
          Current
        </span>
        <Tooltip
          v-if="canRestore && snapshot.id !== currentId"
          :content="restoreDisabledReason ?? 'Restore this version'">
          <span class="ml-auto inline-flex">
            <Button
              variant="ghost"
              size="sm"
              class="h-7 px-2 text-xs opacity-0 group-hover:opacity-100 focus-visible:opacity-100 data-[restoring=true]:opacity-100"
              :data-restoring="restoringId === snapshot.id"
              :disabled="restoringId !== null || restoreDisabledReason !== null"
              @click="emit('restore', snapshot)">
              <Spinner
                v-if="restoringId === snapshot.id"
                class="size-3" />
              <RotateCcw
                v-else
                class="size-3.5" />
              Restore
            </Button>
          </span>
        </Tooltip>
      </li>
    </ul>
  </div>
</template>
